import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { getEstate, deleteEstate } from '../../api/estates';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';

const EstateDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isStaff } = useAuth();

  const [estate, setEstate] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch estate data
  useEffect(() => {
    const fetchEstate = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const data = await getEstate(id);
        setEstate(data);
      } catch (error) {
        console.error('Error fetching estate details:', error);
        setError('Failed to load estate details. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEstate();
  }, [id]);

  // Handle estate deletion
  const handleDeleteEstate = async () => {
    if (!window.confirm('Are you sure you want to delete this estate?')) {
      return;
    }

    try {
      await deleteEstate(id);
      navigate('/members?tab=estates');
    } catch (error) {
      console.error('Error deleting estate:', error);
      alert('Failed to delete estate. Please try again later.');
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error || !estate) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ my: 4 }}>
          <Alert severity="error" sx={{ mb: 3 }}>
            {error || 'Estate not found'}
          </Alert>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            component={RouterLink}
            to="/members?tab=estates"
          >
            Back to Estates
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
          <Box>
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              component={RouterLink}
              to="/members?tab=estates"
              sx={{ mb: 2 }}
            >
              Back to Estates
            </Button>

            <Typography variant="h4" component="h1" gutterBottom>
              {estate.name}
              <Chip
                label={estate.billing_mode === 'estate_pays' ? 'Estate Pays' : 'Members Pay'}
                color={estate.billing_mode === 'estate_pays' ? 'primary' : 'default'}
                sx={{ ml: 2 }}
              />
            </Typography>
          </Box>

          {isStaff() && (
            <Box>
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<EditIcon />}
                component={RouterLink}
                to={`/members/estates/edit/${estate.id}`}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteEstate}
              >
                Delete
              </Button>
            </Box>
          )}
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Estate Information
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Contact Name:</strong> {estate.contact_name || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Contact Phone:</strong> {estate.contact_phone || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Contact Email:</strong> {estate.contact_email || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Address:</strong> {estate.address || 'N/A'}
                </Typography>
                {estate.member_count !== undefined && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Members:</strong> {estate.member_count}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {estate.notes && (
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                    {estate.notes}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>
      </Box>
    </Container>
  );
};

export default EstateDetail;
